import React from "react";
import {
  DataGrid as MuiDataGrid,
  DataGridProps,
  GridColDef,
  GridOverlay,
  GridSlotsComponent,
  GridSlotsComponentsProps,
} from "@material-ui/data-grid";
import { Theme } from "@material-ui/core/styles";
import { styled } from "@material-ui/styles";

import { DataGridRowType } from "../../../modules/BaseDataGrid/BaseDataGrid";

type DesignDataGridProps<T extends GridColDef[]> = {
  columns: T;
  rows: DataGridRowType<T>[];
  components?: GridSlotsComponent;
  componentsProps?: GridSlotsComponentsProps;
  autoHeight?: boolean;
};

const NoRowsMessage = styled("div")(({ theme }: { theme: Theme }) => ({
  fontSize: 14,
  color: theme.palette.brand.gray500,
}));

const DefaultNoRowsOverlay = () => (
  <GridOverlay>
    <NoRowsMessage>No records available.</NoRowsMessage>
  </GridOverlay>
);

const StyledGrid = styled(MuiDataGrid)(({ theme }: { theme: Theme }) => ({
  height: "100%",
  border: "none",
  "& .MuiDataGrid-columnHeaders": {
    background: theme.palette.brand.gray50,
    borderBottom: `1px solid ${theme.palette.brand.gray100}`,
  },
  "& .MuiDataGrid-columnHeaderTitle": {
    textTransform: "uppercase",
    fontSize: 10,
    letterSpacing: "0.08em",
    color: theme.palette.brand.gray500,
    fontWeight: 600,
  },
  "& .MuiDataGrid-row:hover": {
    background: `${theme.palette.brand.gray50} !important`,
  },
  "& .MuiDataGrid-cell": {
    borderBottom: `1px solid ${theme.palette.brand.gray100}`,
    display: "flex",
    alignItems: "center",
  },
  "& .MuiDataGrid-footerContainer": {
    borderTop: `1px solid ${theme.palette.brand.gray100}`,
  },
  "& .primaryCell": {
    fontWeight: 600,
  },
  "& .secondaryCell": {
    fontWeight: 400,
    color: theme.palette.brand.gray500,
  },
}));

const DataGrid = <T extends GridColDef[]>({
  columns,
  rows,
  components,
  componentsProps,
  autoHeight,
  ...rest
}: DesignDataGridProps<T> & DataGridProps) => (
  <StyledGrid
    autoHeight={!!autoHeight}
    rowHeight={48}
    columns={columns}
    rows={rows}
    components={{
      NoRowsOverlay: DefaultNoRowsOverlay,
      ...components,
    }}
    componentsProps={componentsProps}
    isRowSelectable={() => false}
    {...rest}
  />
);

export default DataGrid;
